import * as React from 'react';
import { SyntheticEvent } from 'react';
import { ColumnProps, Input, SortState, Table } from '@rtkwlf/ui';
import Panel from '../../../widgets/Panel';
import { PageTitle } from '../../../widgets/PageTitle';
import Viewport from '../../../widgets/Viewport';
import ViewHeader from '../../../widgets/ViewHeader';
import { ColumnStatus } from './ColumnStatus';
import { ColumnAction } from './ColumnAction';
import {
  DARK_PLACEHOLDER,
  getAlphaComparator,
  getNewValue,
  ROUTE_PATH,
} from '../../../constants';
import { Link } from 'react-router-dom';
import InputDropdown from '../../../widgets/InputDropdown';
import ErrorJumbotron from '../../../widgets/ErrorJumbotron';
import { License, OptionType, VirtualLogCollector } from '../../../types';
import { withTrashableContext } from '../../../hooks';
import { ValueType } from 'react-select/src/types';
import { useDecommissionSensor } from './useDecommissionSensor';
import { useRegisterSensor } from './useRegisterSensor';
import { useFetchVirtualSensors } from './useFetchVirtualSensors';
import AvailableSensors from '../../../widgets/AvailableSensors';
import Icon from '../../../widgets/Icons';
import './VirtualSensor.scss';
import { Button, Tooltip } from '@rtkwlf/fenrir-react';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../reducers/session';
import { useDispatch, useSelector } from 'react-redux';
import { apiRequest } from '../../../APIClient';

const ANY = '*';

const ALL_FILTER = { value: ANY, label: 'Display All' };

const PLATFORM_TYPES = [
  ALL_FILTER,
  { value: 'vmware', label: 'VMware ESXi' },
  { value: 'aws', label: 'AWS' },
  { value: 'vanilla', label: 'Physical Appliance' },
  { value: 'azure', label: 'Azure' },
  { value: 'hyper-v', label: 'Hyper-V' },
  { value: 'nutanix', label: 'Nutanix' },
];

const VLC_STATES = [
  ALL_FILTER,
  { value: 'activated', label: 'Not Activated' },
  { value: 'registered', label: 'Activated' },
];

const DEPLOYMENT_TYPES = [
  ALL_FILTER,
  { value: 'vlc', label: 'Virtual Log Collector' },
  { value: 'mirroring', label: 'Mirroring' },
  { value: 'scanner', label: 'Scanner' },
  { value: 'internal_tap', label: 'Internal Tap' },
];

export const PLATFORM_INFO = {
  awnv100: 'VMware ESXi',
  awnv200: 'VMware ESXi',
  awnv1000: 'VMware ESXi',
  vmware: 'VMware ESXi',
  vlc: 'VMware ESXi',
  aws_awnv100: 'AWS',
  aws_awnv200: 'AWS',
  aws_awnv1000: 'AWS',
  vanilla: 'Physical Appliance',
  azure_awnv100: 'Azure',
  azure_awnv200: 'Azure',
  azure_awnv1000: 'Azure',
  hyperv_awnv100: 'Hyper-V',
  hyperv_awnv200: 'Hyper-V',
  hyperv_awnv1000: 'Hyper-V',
  nutanix_awnv100: 'Nutanix',
  nutanix_awnv200: 'Nutanix',
  nutanix_awnv1000: 'Nutanix',
  default: 'VMware ESXi',
};

export const DEPLOYMENT_TYPE_INFO = {
  vlc: 'Virtual Log Collector',
  mirroring: 'Mirroring',
  scanner: 'Scanner',
  internal_tap: 'Internal Tap',
  default: 'Virtual Log Collector',
};

export const VIRTUAL_SENSOR_TYPES = {
  awnv100: 'AWNv100',
  awnv200: 'AWNv200',
  awnv1000: 'AWNv1000',
  aws_awnv100: 'AWNv100',
  vmware: 'AWNv200',
};

export const getProduct = (sensor: VirtualLogCollector): string => {
  if (
    sensor.product_type &&
    (sensor.product_type.toLowerCase() === 'vsensor' ||
      sensor.product_type.toLowerCase() === 'vlc')
  ) {
    if (
      sensor.model_size &&
      sensor.model_size.toLowerCase() in VIRTUAL_SENSOR_TYPES
    ) {
      return VIRTUAL_SENSOR_TYPES[
        sensor.model_size.toLowerCase() as keyof typeof VIRTUAL_SENSOR_TYPES
      ];
    } else if (
      sensor.hardware_type &&
      sensor.hardware_type.toLowerCase() in VIRTUAL_SENSOR_TYPES
    ) {
      return VIRTUAL_SENSOR_TYPES[
        sensor.hardware_type.toLowerCase() as keyof typeof VIRTUAL_SENSOR_TYPES
      ];
    } else if (sensor.hardware_type) {
      return sensor.hardware_type.toUpperCase();
    }
    return VIRTUAL_SENSOR_TYPES.awnv200;
  } else if (sensor.hardware_type) {
    return sensor.hardware_type.toUpperCase();
  }
  return '';
};

const getProductTypeComparator = () => {
  return (a: VirtualLogCollector, b: VirtualLogCollector): number => {
    return getProduct(a).localeCompare(getProduct(b));
  };
};

const getDeploymentTypeComparator = () => {
  return (a: VirtualLogCollector, b: VirtualLogCollector): number => {
    return DEPLOYMENT_TYPE_INFO[a.deployment_type ?? 'default'].localeCompare(
      DEPLOYMENT_TYPE_INFO[b.deployment_type ?? 'default'],
    );
  };
};

export const getPlatform = (sensor: VirtualLogCollector): string => {
  if (sensor.is_vanilla) return 'vanilla';
  if (!sensor.hardware_type) return 'default';
  return sensor.hardware_type.toLowerCase();
};

const getPlatformComparator = () => {
  return (a: VirtualLogCollector, b: VirtualLogCollector): number => {
    return (
      PLATFORM_INFO[getPlatform(a) as keyof typeof PLATFORM_INFO] ??
      PLATFORM_INFO.default
    ).localeCompare(
      PLATFORM_INFO[getPlatform(b) as keyof typeof PLATFORM_INFO] ??
        PLATFORM_INFO.default,
    );
  };
};

type SortOrder = {
  column:
    | 'short_name'
    | 'serial_number'
    | 'product_type'
    | 'deployment_type'
    | 'platform'
    | 'state';
  reverse: boolean;
};

const SORTER = {
  short_name:
    getAlphaComparator<Omit<VirtualLogCollector, 'is_vanilla' | 'profile_id'>>(
      'short_name',
    ),
  serial_number:
    getAlphaComparator<Omit<VirtualLogCollector, 'is_vanilla' | 'profile_id'>>(
      'serial_number',
    ),
  platform: getPlatformComparator(),
  product_type: getProductTypeComparator(),
  deployment_type: getDeploymentTypeComparator(),
  state:
    getAlphaComparator<Omit<VirtualLogCollector, 'is_vanilla' | 'profile_id'>>(
      'state',
    ),
};

const VanillaSensorPageTitleAndHeader = () => (
  <>
    <PageTitle title="Arctic Wolf Appliance Management" />
    <ViewHeader title="Arctic Wolf Appliance Management" />
  </>
);

const VanillaSensorListView: React.FC = () => {
  const { error, isLoading, vSensors, getVirtualLogCollectorList } =
    useFetchVirtualSensors();

  const { modal: deleteModal, onConfirmDelete } = useDecommissionSensor(
    getVirtualLogCollectorList,
  );
  const { modal: registerModal, onConfirmRegister } = useRegisterSensor(
    getVirtualLogCollectorList,
    vSensors,
  );

  const dispatch = useDispatch();

  const platformColumnEnabled = useSelector(featureFlagSelector.platformColumn);
  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';

  const [sortOrder, setSortOrder] = React.useState<SortOrder | null>(null);
  const [textFilter, setTextFilter] = React.useState('');
  const [deploymentFilter, setDeploymentFilter] = React.useState<OptionType[]>(
    [],
  );
  const [platformFilter, setPlatformFilter] = React.useState<OptionType[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<OptionType | null>(
    null,
  );

  const columns: ColumnProps<VirtualLogCollector>[] = [
    {
      key: 'short_name',
      dataIndex: 'short_name',
      title: 'Name',
      width: 18,
      justify: 'flex-start',
      sortable: true,
    },
    {
      key: 'serial_number',
      dataIndex: 'serial_number',
      title: 'Serial Number',
      width: platformColumnEnabled ? 17 : 20,
      justify: 'flex-start',
      sortable: true,
    },
    {
      key: 'product_type',
      dataIndex: 'product_type',
      title: 'Model',
      width: platformColumnEnabled ? 16 : 20,
      justify: 'flex-start',
      render: ({ record: sensor }) => (
        <div className="d-flex">
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div className="m-auto">
                {sensor.is_vanilla ? (
                  <Icon.PhysicalSensor size={20} />
                ) : sensor.product_type === 'scanner' ? (
                  <Icon.Scanner size={16} />
                ) : (
                  <Icon.VirtualSensor size={20} />
                )}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content side={'top'}>
              <Tooltip.Text>
                {sensor.is_vanilla ? 'Physical device' : 'Virtual device'}
              </Tooltip.Text>
            </Tooltip.Content>
          </Tooltip.Root>
          <span className="ml-2">{getProduct(sensor)}</span>
        </div>
      ),
      sortable: true,
    },
    {
      key: 'deployment_type',
      dataIndex: 'deployment_type',
      title: 'Configuration',
      width: platformColumnEnabled ? 18 : 20,
      justify: 'flex-start',
      render: ({ record: sensor }) => (
        <span>{DEPLOYMENT_TYPE_INFO[sensor.deployment_type ?? 'default']}</span>
      ),
      sortable: true,
    },
    {
      key: 'state',
      title: 'Status',
      width: platformColumnEnabled ? 17 : 20,
      justify: 'flex-start',
      render: ({
        record: { state, product_type: productType, is_vanilla: isVanilla },
      }) => (
        <ColumnStatus
          state={state}
          productType={productType}
          isVanilla={isVanilla}
        />
      ),
      sortable: true,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 15,
      justify: 'flex-start',
      render: ({ record }) => (
        <ColumnAction
          vlc={record}
          onRegister={handleVlcRegister}
          onDecommission={onConfirmDelete}
        />
      ),
    },
  ];

  if (platformColumnEnabled) {
    columns.splice(2, 0, {
      key: 'platform',
      dataIndex: 'platform',
      title: 'Platform',
      width: 20,
      justify: 'flex-start',
      render: ({ record: sensor }) => {
        const platform =
          PLATFORM_INFO[getPlatform(sensor) as keyof typeof PLATFORM_INFO] ??
          PLATFORM_INFO.default;
        return (
          <div className="d-flex">
            <div className="m-auto">
              {platform === 'AWS' ? (
                <Icon.AWS size={20} />
              ) : platform === 'Azure' ? (
                <Icon.Azure size={20} />
              ) : platform === 'Physical Appliance' ? (
                <Icon.AvailableSensor size={16} className="mr-1" />
              ) : platform === 'Nutanix' ? (
                <Icon.Nutanix size={20} />
              ) : platform === 'Hyper-V' ? (
                <Icon.HyperV size={20} />
              ) : (
                <Icon.VMware size={20} />
              )}
            </div>
            <span className="ml-2">{platform}</span>
          </div>
        );
      },
      sortable: true,
    });
  }

  const handleTextFilterChange = React.useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      setTextFilter(event.currentTarget.value);
    },
    [setTextFilter],
  );

  const handlePlatformFilterChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      setPlatformFilter(getNewValue<OptionType>(value, true) || []);
    },
    [setPlatformFilter],
  );

  const handleDeploymentFilterChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      setDeploymentFilter(getNewValue<OptionType>(value, true) || []);
    },
    [setDeploymentFilter],
  );

  const handleStateFilterChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      setStatusFilter(getNewValue<OptionType>(value, false) || ALL_FILTER);
    },
    [setStatusFilter],
  );

  const handleVlcRegister = React.useCallback(
    (vlcToRegister: VirtualLogCollector) => {
      let productType = '';
      if (vlcToRegister.is_vanilla) {
        productType = 'vanilla';
      } else {
        const tempProductType = getProduct(vlcToRegister).toLowerCase();
        if (tempProductType in VIRTUAL_SENSOR_TYPES) {
          productType = tempProductType;
        }
      }
      let license: License | undefined;
      if (productType !== '') {
        const entry = Object.entries(vSensors).find(
          ([key]) => key === productType,
        );
        if (entry) {
          const [, value] = entry;
          license = value as License;
        }
      }
      if (
        vlcToRegister.deployment_type !== 'scanner' && // scanners are unlimited
        vlcToRegister.deployment_type !== 'vlc' && // vlcs are unlimited
        license &&
        license.used >= license.quota
      ) {
        dispatch(
          apiRequest.setGlobalStatus(
            `${
              productType === 'vanilla' ? 'Physical Appliance' : productType
            } license count exceeded. Can not activate
          ${
            vlcToRegister.is_vanilla
              ? ' physical appliance'
              : ' virtual appliance'
          } ${vlcToRegister.serial_number}.`,
            'danger',
          ),
        );
      } else {
        onConfirmRegister(vlcToRegister);
      }
    },
    [vSensors],
  );

  const handleReset = React.useCallback(() => {
    setTextFilter('');
    setDeploymentFilter([]);
    setPlatformFilter([]);
    setStatusFilter(null);
  }, [setTextFilter, setStatusFilter]);

  const handleSort = React.useCallback(
    (column, state: SortState) => {
      if (state === 'none') {
        setSortOrder(null);
      } else {
        setSortOrder({
          column,
          reverse: state === 'dsc',
        });
      }
    },
    [setSortOrder],
  );

  const quotas = React.useMemo(() => {
    const results = vSensors?.results ?? [];
    const { quota: vlcQuota = 0, used: vlcUsed = 0 } =
      vSensors?.vlc ?? vSensors?.vLC ?? {};
    const { quota: awnv100Quota = 0, used: awnv100Used = 0 } =
      vSensors?.awnv100 ?? {};
    const { quota: awnv200Quota = 0, used: awnv200Used = 0 } =
      vSensors?.awnv200 ?? vSensors?.vsensor ?? vSensors?.vSensor ?? {};
    const { quota: awnv1000Quota = 0, used: awnv1000Used = 0 } =
      vSensors?.awnv1000 ?? {};
    const { quota: vanillaQuota = 0, used: vanillaUsed = 0 } =
      vSensors?.vanilla ?? {};
    const vlc = {
      used: vlcUsed,
      available: vlcQuota - vlcUsed,
      quota: vlcQuota,
    };
    const awnv100 = {
      used: awnv100Used,
      available: awnv100Quota - awnv100Used,
      quota: awnv100Quota,
    };
    const awnv200 = {
      used: awnv200Used,
      available: awnv200Quota - awnv200Used,
      quota: awnv200Quota,
    };
    const awnv1000 = {
      used: awnv1000Used,
      available: awnv1000Quota - awnv1000Used,
      quota: awnv1000Quota,
    };
    const vanilla = {
      used: vanillaUsed,
      available: vanillaQuota - vanillaUsed,
      quota: vanillaQuota,
    };
    const total = results?.length || 0;

    return {
      total,
      vlc,
      awnv100,
      awnv200,
      awnv1000,
      vanilla,
    };
  }, [vSensors]);

  // Filter by state
  let filteredSensors: VirtualLogCollector[] = [];

  if (Array.isArray(vSensors?.results) && quotas.total > 0) {
    filteredSensors =
      statusFilter?.value === ANY || !statusFilter
        ? vSensors.results
        : vSensors.results.filter(
            (vSensor) => vSensor.state === statusFilter.value,
          );

    // Then filter by platform
    const selectedPlatforms = platformFilter.map((a) => a.value);
    filteredSensors =
      platformFilter?.includes(ALL_FILTER) || platformFilter.length === 0
        ? filteredSensors
        : filteredSensors.filter((vSensor) => {
            const platform = getPlatform(vSensor) as keyof typeof PLATFORM_INFO;
            const platformInfo =
              PLATFORM_INFO[platform] ?? PLATFORM_INFO.default;
            if (platformInfo.includes('AWS'))
              return selectedPlatforms.includes('aws');
            if (platformInfo.includes('Azure'))
              return selectedPlatforms.includes('azure');
            if (platformInfo.includes('Nutanix'))
              return selectedPlatforms.includes('nutanix');
            if (platformInfo.includes('Hyper-V'))
              return selectedPlatforms.includes('hyper-v');
            else if (platformInfo.includes('VMware'))
              return selectedPlatforms.includes('vmware');
            else return selectedPlatforms.includes(platform);
          });

    // Then filter by deployment type
    const selectedDeployments = deploymentFilter.map((a) => a.value);
    filteredSensors =
      deploymentFilter?.includes(ALL_FILTER) || deploymentFilter.length === 0
        ? filteredSensors
        : filteredSensors.filter((vSensor) =>
            selectedDeployments.includes(
              (vSensor.deployment_type ?? 'vlc').toLowerCase(),
            ),
          );

    // Then filter by text match
    filteredSensors = filteredSensors.filter((sensor) =>
      `${sensor.serial_number}\n${sensor.short_name}`
        .toLocaleLowerCase()
        .includes(textFilter.toLocaleLowerCase()),
    );
  }

  // Then sort
  if (sortOrder) {
    filteredSensors.sort(SORTER[sortOrder.column]);
    if (sortOrder.reverse) {
      filteredSensors.reverse();
    }
  }

  let emptyComponent: React.ReactNode;
  if (error) {
    emptyComponent = <ErrorJumbotron error={error} />;
  } else if (quotas.total === 0) {
    emptyComponent = (
      <div className="sensor-no-results-container">
        <Icon.Error size={46} />
        <div className="mt-3">No sensors are installed</div>
        <span className="mt-2 mb-3">
          Go to{' '}
          <a
            href={ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL + orgIDParam}
            target="_blank"
            rel="noopener noreferrer"
          >
            Downloads
          </a>{' '}
          to begin downloading and installing Virtual Sensors.
        </span>
      </div>
    );
  } else {
    emptyComponent = (
      <div className="sensor-no-results-container">
        <Icon.Error size={46} />
        <div className="mt-3">No results to display</div>
        <span className="mt-2 mb-3">
          Try a different filter option or search term
        </span>
        <Button variant="primary" style={{ height: 38 }} onClick={handleReset}>
          Clear Filters
        </Button>
      </div>
    );
  }

  return (
    <Viewport>
      <VanillaSensorPageTitleAndHeader />
      <Panel className="available-sensors-panel">
        <div className="available-sensors-icon-text">
          <div className="available-sensors-icon-container">
            <Icon.AvailableSensor className="available-sensors-icon" />
          </div>
          <div className="available-sensors-text">
            <h2 className="mb-2" style={{ color: '#58595B' }}>
              Available Virtual Sensors
            </h2>
            <div style={{ color: '#58595B' }}>
              Virtual sensors must be activated to provide security insights.{' '}
              <Link
                style={{ fontWeight: 'bold', textDecoration: 'underline' }}
                to={ROUTE_PATH.CONTACT_CST}
              >
                Contact your CST
              </Link>{' '}
              to request additional activation licenses for Virtual Sensors.
            </div>
          </div>
        </div>
        <div className="available-sensors-container">
          <AvailableSensors
            value={`${quotas.awnv100.available}/${quotas.awnv100.quota}`}
            appliance={'AWNv100'}
            active={quotas.awnv100.available !== 0}
          />
          <AvailableSensors
            value={`${quotas.awnv200.available}/${quotas.awnv200.quota}`}
            appliance={'AWNv200'}
            active={quotas.awnv200.available !== 0}
          />
          <AvailableSensors
            value={`${quotas.awnv1000.available}/${quotas.awnv1000.quota}`}
            appliance={'AWNv1000'}
            active={quotas.awnv1000.available !== 0}
          />
        </div>
      </Panel>
      <Panel className="mt-3 mr-2 mb-2">
        <div className="d-flex flex-column mb-4">
          <div className="sensor-filters-container">
            <div className="sensor-text-filter">
              <h5 className="mb-1">Search</h5>
              <Input
                name="textFilter"
                placeholder="Type to begin filtering"
                id="textFilter"
                value={textFilter}
                onChange={handleTextFilterChange}
                disabled={quotas.total === 0}
              />
            </div>
            <div className="sensor-filters-row-2">
              {platformColumnEnabled && (
                <div style={{ width: 270 }} className="pr-3">
                  <h5 className="mb-1">Platform</h5>
                  <InputDropdown
                    name="platformFilter"
                    isClearable={false}
                    isCreatable={false}
                    placeholder={
                      <div style={{ color: '#CFD4D9' }}>Choose platforms</div>
                    }
                    isMultiEllipsis={true}
                    onChange={handlePlatformFilterChange}
                    options={PLATFORM_TYPES}
                    value={platformFilter}
                    styles={DARK_PLACEHOLDER}
                    isDisabled={quotas.total === 0}
                    showDropdownArrow={true}
                  />
                </div>
              )}
              <div style={{ width: 270 }} className="pr-3">
                <h5 className="mb-1">Configuration</h5>
                <InputDropdown
                  name="deploymentFilter"
                  isClearable={false}
                  isCreatable={false}
                  placeholder={
                    <div style={{ color: '#CFD4D9' }}>
                      Choose configurations
                    </div>
                  }
                  isMultiEllipsis={true}
                  onChange={handleDeploymentFilterChange}
                  options={DEPLOYMENT_TYPES}
                  value={deploymentFilter}
                  styles={DARK_PLACEHOLDER}
                  isDisabled={quotas.total === 0}
                  showDropdownArrow={true}
                />
              </div>
              <div style={{ width: 200 }} className="pr-3">
                <h5 className="mb-1">Status</h5>
                <InputDropdown
                  name="statusFilter"
                  placeholder={
                    <div style={{ color: '#CFD4D9' }}>All statuses</div>
                  }
                  isClearable={false}
                  isCreatable={false}
                  onChange={handleStateFilterChange}
                  options={VLC_STATES}
                  value={statusFilter}
                  styles={DARK_PLACEHOLDER}
                  isDisabled={quotas.total === 0}
                  showDropdownArrow={true}
                />
              </div>
              <div className="clear-filters-button">
                {textFilter !== '' ||
                deploymentFilter.length !== 0 ||
                platformFilter.length !== 0 ||
                statusFilter !== null ? (
                  <Button
                    variant="secondary"
                    style={{ height: 38 }}
                    onClick={handleReset}
                  >
                    Clear Filters
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          <span className="sensor-results mt-3">
            {filteredSensors.length === quotas.total ? (
              <>
                Total Sensors{' '}
                <span>
                  ({filteredSensors.length} of {quotas.total})
                </span>
              </>
            ) : (
              <>
                Results Displayed{' '}
                <span>
                  ({filteredSensors.length} of {quotas.total})
                </span>
              </>
            )}
          </span>
        </div>
        {isLoading || filteredSensors.length > 0 ? (
          <>
            <Table
              className="virtual-collector-table sticky-table"
              columns={columns}
              data={filteredSensors}
              dataUniqueKey="value"
              loadingComponent={'Loading Sensors...'}
              loading={isLoading}
              onSort={handleSort}
            />
            <div className="mt-3 text-center">
              Sensors must be activated to provide security insights.
            </div>
          </>
        ) : (
          emptyComponent
        )}
      </Panel>
      {deleteModal}
      {registerModal}
    </Viewport>
  );
};

export default withTrashableContext(VanillaSensorListView);
